import { ZonedDateTime } from '@internationalized/date';

interface DateRange {
  start: ZonedDateTime;
  end: ZonedDateTime;
}

export const validateDateRange = (dateRange: DateRange): void => {
  if (!dateRange.start || !dateRange.end) {
    throw new Error('DateTimeRangePicker: start and end dates are required');
  }

  // Check if they are ZonedDateTime objects by checking for required properties
  if (!dateRange.start.timeZone || !dateRange.end.timeZone) {
    throw new Error(
      'DateTimeRangePicker: start and end must be ZonedDateTime objects',
    );
  }

  if (dateRange.start.compare(dateRange.end) >= 0) {
    throw new Error('DateTimeRangePicker: start date must be before end date');
  }
};

export const validateMinDate = (date: ZonedDateTime | undefined): void => {
  if (!date) return;

  if (!date.timeZone) {
    throw new Error(
      'DateTimeRangePicker: minDate must be a valid ZonedDateTime object',
    );
  }
};

export const createDateRangeValidator = (minDate?: ZonedDateTime) => {
  return (range: DateRange): string => {
    if (range.start.compare(range.end) >= 0) {
      return 'Start date must be before end date';
    }

    if (minDate && range.start.compare(minDate) < 0) {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: minDate.timeZone,
        dateStyle: 'short',
      });
      return `Start date cannot be before ${formatter.format(minDate.toDate())}`;
    }

    if (minDate && range.end.compare(minDate) < 0) {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: minDate.timeZone,
        dateStyle: 'short',
      });
      return `End date cannot be before ${formatter.format(minDate.toDate())}`;
    }

    return '';
  };
};

export const validateDateTimeRange = (
  range: DateRange,
): { isValid: boolean; error?: string } => {
  if (!range.start || !range.end) {
    return { isValid: false, error: 'Start and end dates are required' };
  }

  if (!range.start.timeZone || !range.end.timeZone) {
    return {
      isValid: false,
      error: 'Start and end must be ZonedDateTime objects',
    };
  }

  if (range.start.compare(range.end) >= 0) {
    return { isValid: false, error: 'Start date must be before end date' };
  }

  return { isValid: true };
};
